import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { formatBareDate } from '@/lib/etDate';

interface Game {
  game_pk: number;
  game_date: string;
  home_team_name: string;
  away_team_name: string;
  status: string;
  start_time: string | null;
  venue: string | null;
  last_fetched_at: string | null;
}

const MLBBetaSchedule = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('game_schedule')
        .select('*')
        .eq('game_date', selectedDate)
        .order('start_time', { ascending: true });

      if (!error) setGames((data as Game[]) || []);
      setLoading(false);
    };
    fetchGames();
  }, [selectedDate]);

  const statusColor = (s: string) => {
    switch (s) {
      case 'live': return 'bg-destructive text-destructive-foreground';
      case 'final': return 'bg-accent text-accent-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const changeDate = (offset: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground">Game Schedule</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => changeDate(-1)} className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80">←</button>
          <span className="text-sm font-medium text-foreground min-w-[100px] text-center">
            {formatBareDate(selectedDate, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <button onClick={() => changeDate(1)} className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80">→</button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : games.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center text-muted-foreground">
            No games found for this date. Try fetching the schedule first.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {games.map((game) => (
            <Card key={game.game_pk} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground">{game.away_team_name}</span>
                      <span className="text-xs text-muted-foreground">@</span>
                      <span className="text-sm font-semibold text-foreground">{game.home_team_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {game.venue && <span>{game.venue}</span>}
                      {game.start_time && (
                        <span>• {format(new Date(game.start_time), 'h:mm a')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`text-[10px] ${statusColor(game.status)}`}>
                      {game.status.toUpperCase()}
                    </Badge>
                    {game.last_fetched_at && (
                      <span className="text-[10px] text-muted-foreground">
                        Fetched {format(new Date(game.last_fetched_at), 'h:mm a')}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          <p className="text-xs text-muted-foreground text-center pt-2">
            {games.length} game{games.length !== 1 ? 's' : ''} on {formatBareDate(selectedDate, { month: 'short', day: 'numeric' })}
          </p>
        </div>
      )}
    </div>
  );
};

export default MLBBetaSchedule;
